/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import React, { forwardRef, useImperativeHandle } from 'react';
import { SELECTION_MODE_MULTIPLE, SELECTION_MODE_SINGLE } from '_utils/constant';
import { useSubItems } from '../../hooks';
import './style.scss';
import HelpInformation from '../HelpInformation';

// eslint-disable-next-line react/display-name
const SubItems = forwardRef((
  { initialGroups, onChange, onDelete, error, hasOrderCount = false },
  ref
) => {
  const {
    groups,
    isHidden,
    toggleHidden,
    handleAddSubItem,
    handleRemoveGroup,
    handleGroupTitleChange,
    handleSelectionModeChange, // Temporarily disabled for single sub-item
    handleRequiredToggle,
    handleAddChoice,
    handleRemoveChoice,
    handleChoiceChange,
  } = useSubItems({ initialGroups, onChange });

  useImperativeHandle(ref, () => ({
    handleAddSubItem,
  }));

  const handlePriceKeyDown = (e) => {
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ].includes(e.key) ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return;
    }

    if (e.key === '.' && !e.currentTarget.value.includes('.')) {
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="sub-container">
      <div className="sub-container-header">
        <div className="sub-container-title">
          Sub-Items
          <HelpInformation content="Lets buyers pick options for this product before ordering — like Frame Color, Size, or Toppings. Give the group a title, then add its options underneath. Each row below (Selection Mode, Required) has its own &quot;?&quot; with more detail."/>
        </div>
        <div className="sub-container-actions">
          <span
            className="btn-action-text btn-action-text--highlight"
            onClick={toggleHidden}
            role="button"
            tabIndex={0}
          >
            {isHidden ? 'Show' : 'Hide'}
          </span>
          {onDelete && (
            <div
              className="icon-btn danger"
              title="Delete"
              onClick={onDelete}
              role="button"
              tabIndex={0}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {!isHidden && (
        <>
          <div className="sub-groups-wrap">
            {groups.map((group, gIdx) => {
              const groupError = error?.[gIdx]?.list;

              return (
              <div key={group.id} className="sub-group-card">
                <div className="sub-group-title-row">
                  <input
                    type="text"
                    placeholder="Sub-item title (e.g. Frame Color)"
                    value={group.title}
                    onChange={(e) => handleGroupTitleChange(group.id, e.target.value)}
                  />
                  <div
                    className="icon-btn danger"
                    title="Delete group"
                    onClick={() => {
                      if (onDelete) onDelete();
                      else handleRemoveGroup(group.id);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                  </div>
                </div>

                <div className="sub-group-headers">
                  <span className="hdr-name">Choice name</span>
                  <span className="hdr-cost">Add-on cost</span>
                  {group.trackInventory && (
                    <span className="hdr-max-qty">Available stock <span style={{ color: 'red' }}>*</span></span>
                  )}
                  <span className="hdr-spacer" />
                </div>

                <div className="sub-group-rows">
                  {group.choices.map((choice, cIdx) => {
                    const choiceMaxQtyError = groupError?.[cIdx]?.maxQty?.message;
                    return (
                      <>
                        <div key={choice.id} className="subitem-row">
                          <input
                            name="input-subitem-name"
                            className="subitem-name"
                            type="text"
                            placeholder="e.g. Blue"
                            value={choice.name}
                            onChange={(e) =>
                              handleChoiceChange(group.id, choice.id, 'name', e.target.value)
                            }
                          />
                          <span className="subitem-dollar-sign">+$</span>
                          <input
                            name="input-price"
                            className="subitem-price"
                            type="number"
                            placeholder="0"
                            step="0.5"
                            min="0"
                            value={choice.price}
                            onKeyDown={handlePriceKeyDown}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (val !== '') {
                                val = val.replace(/[^0-9.]/g, '');
                                const parts = val.split('.');
                                if (parts.length > 2) {
                                  val = `${parts[0]}.${parts.slice(1).join('')}`;
                                }
                              }
                              handleChoiceChange(group.id, choice.id, 'price', val);
                            }}
                          />
                          {group.trackInventory && (
                            <input
                              name='input-max-qty'
                              className={`subitem-max-qty ${choiceMaxQtyError ? 'error-input' : ''}`}
                              type="number"
                              placeholder="Limit"
                              min="1"
                              value={choice.maxQty}
                              onKeyDown={handlePriceKeyDown}
                              onChange={(e) => {
                                let val = e.target.value;
                                if (val !== '') {
                                  val = val.replace(/[^0-9]/g, '');
                                }
                                handleChoiceChange(group.id, choice.id, 'maxQty', val);
                              }}
                            />
                          )}
                          <div
                            className="subitem-del"
                            title="Remove choice"
                            onClick={() => handleRemoveChoice(group.id, choice.id)}
                            role="button"
                            tabIndex={0}
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </div>
                        </div>
                        {choiceMaxQtyError && (
                          <span className="error-text">
                            {choiceMaxQtyError}
                          </span>
                        )}
                      </>
                    );
                  })}
                </div>
                <div
                  className="add-choices-btn"
                  onClick={() => handleAddChoice(group.id)}
                  role="button"
                  tabIndex={0}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 1v10M1 6h10" />
                  </svg>{' '}
                  Add choices
                </div>

                <div className="selection-mode-row">
                  <span>
                    Selection Mode
                    <HelpInformation content={`
                      <strong>Choose One: </strong>the buyer picks one option from this group, then sets how many of it they want (e.g. one Frame Color, then a quantity).
                      <br /><strong>Build a Combo: </strong>the buyer picks several options, then sets one overall quantity for that combination (e.g. 2 pearl + 2 jelly, then ×2 teas).
                      <br /><strong>Choose Quantities: </strong>the buyer sets a separate quantity for each option — no overall total to set, they just add up (e.g. 10 small + 2 medium + 5 large = 17 total).`} />
                  </span>
                  <div className={`selection-toggle-pill ${hasOrderCount ? 'disabled' : ''}`}>
                    <button
                      type="button"
                      disabled={hasOrderCount}
                      className={(group.selectionMode === SELECTION_MODE_SINGLE && !group.trackInventory) ? 'active' : ''}
                      onClick={
                        () => !hasOrderCount && handleSelectionModeChange(group.id, SELECTION_MODE_SINGLE, false)
                      }
                    >
                      Choose One
                    </button>
                    <button
                      type="button"
                      disabled={hasOrderCount}
                      className={(group.selectionMode === SELECTION_MODE_MULTIPLE && !group.trackInventory) ? 'active' : ''}
                      onClick={
                        () => !hasOrderCount && handleSelectionModeChange(group.id, SELECTION_MODE_MULTIPLE, false)
                      }
                    >
                      Build a Combo
                    </button>
                    <button
                      type="button"
                      disabled={hasOrderCount}
                      className={(group.selectionMode === SELECTION_MODE_SINGLE && group.trackInventory) ? 'active' : ''}
                      onClick={
                        () => !hasOrderCount && handleSelectionModeChange(group.id, SELECTION_MODE_SINGLE, true)
                      }
                    >
                      Choose Quantities
                    </button>
                  </div>
                </div>

                <SubItemModePreview
                  selectionMode={group.selectionMode}
                  trackInventory={group.trackInventory}
                />

                <div className="required-row">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>Required</span>
                    <HelpInformation content='If on, the buyer must select at least one option from this group before they can complete their order.'/>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <div
                      className={`toggle-sw ${group.required ? 'on' : ''}`}
                      onClick={() => handleRequiredToggle(group.id)}
                      role="button"
                      tabIndex={0}
                    />
                    {error?.[gIdx]?.required?.message && (
                      <span className="error-text" style={{ marginRight: 0, marginTop: '8px' }}>
                        {error[gIdx].required.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>

        </>
      )}
    </div>
  );
});

function SubItemModePreview({ selectionMode = SELECTION_MODE_SINGLE, trackInventory = false }) {
  if (selectionMode === SELECTION_MODE_SINGLE && !trackInventory) {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-chk-row">
              <div className="subprev-radio" />
            </div>
            <div className="subprev-chk-row">
              <div className="subprev-radio on" />
            </div>
            <div className="subprev-ov">
              <div className="subprev-bc minus">−</div>
              <span className="subprev-bv">2</span>
              <div className="subprev-bc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Choose one option for this product, then set how many of this combination you want.
          </span>
        </div>
      </div>
    );
  }

  if (selectionMode === SELECTION_MODE_MULTIPLE && !trackInventory) {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-chk-row">
              <div className="subprev-chk" />
              <span className="subprev-mv">2</span>
            </div>
            <div className="subprev-chk-row">
              <div className="subprev-chk" />
              <span className="subprev-mv">2</span>
            </div>
            <div className="subprev-ov">
              <div className="subprev-bc minus">−</div>
              <span className="subprev-bv">2</span>
              <div className="subprev-bc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Choose multiple options, then set how many of this combination you want.
          </span>
        </div>
      </div>
    );
  }

  if (selectionMode === SELECTION_MODE_SINGLE && trackInventory) {
    return (
      <div className="sub-item-mode-preview">
        <div className="subprev-container">
          <div className="subprev-diagram">
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">10</span>
              <div className="subprev-sc plus">+</div>
            </div>
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">2</span>
              <div className="subprev-sc plus">+</div>
            </div>
            <div className="subprev-sp-row">
              <div className="subprev-sc minus">−</div>
              <span className="subprev-sv">5</span>
              <div className="subprev-sc plus">+</div>
            </div>
          </div>
          <span className="subprev-text">
            Set a separate quantity for each option.
          </span>
        </div>
      </div>
    );
  }

  return null;
}

export default SubItems;
